<template>
  <card-dialog
    v-model="modalVisible"
    max-width="400px"
    :title="matchOpponentFormData.id ? $t('card_title.edit_match_opponent') : $t('card_title.add_match_opponent') "
  >
    <v-card-text
      class="pa-6"
    >
      <validation-observer ref="validation">
        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-text-field
            v-model="matchOpponentFormData.name"
            :error-messages="errors"
            :label="$t('text_input.match_opponent_name.title')"
            :placeholder="$t('text_input.match_opponent_name.placeholder')"
            outlined
            dense
          />
        </validation-provider>
      </validation-observer>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4">
        <div class="flex-grow-1">
          <v-btn
            color="primary"
            block
            large
            :loading="submitting"
            @click="submit()"
          >
            {{ $t('training_builder.button.save') }}
          </v-btn>
          <v-btn
            v-if="matchOpponentFormData.id"
            color="error"
            text
            block
            class="mt-1"
            @click="toggleConfirmationDialog(true)"
          >
            {{ $t('button.delete') }}
          </v-btn>
        </div>
      </v-card-actions>
    </template>

    <delete-confirmation-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirmed="deleteMatchOpponent()"
    />
  </card-dialog>
</template>

<script>
import CardDialog from '@/components/helpers/CardDialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import { MatchOpponentApiService } from '@/data/services'
import { EventBus } from '@/plugins/bus'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog'
import { cloneDeep } from 'lodash'

const emptyMatchOpponentFormData = {
  id: undefined,
  name: ''
}

export default {
  name: 'MatchOpponentFormDialog',
  components: { DeleteConfirmationDialog, CardDialog },
  mixins: [deleteConfirmationMixin],
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      matchOpponentFormData: cloneDeep(emptyMatchOpponentFormData),
      submitting: false
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    }
  },
  created() {
    EventBus.$on(this.$events.OPEN_DIALOG_MATCH_OPPONENT_FORM, () => {
      this.modalVisible = true
      this.matchOpponentFormData = cloneDeep(emptyMatchOpponentFormData)

      this.$nextTick(() => {
        this.$refs.validation.reset()
      })
    })

    EventBus.$on(this.$events.EDIT_MATCH_OPPONENT, (matchOpponent) => {
      this.modalVisible = true

      this.matchOpponentFormData = {
        id: matchOpponent.id,
        name: matchOpponent.name
      }
    })
  },
  methods: {
    async submit() {
      if (!(await this.$refs.validation.validate())) return

      await this.save()
    },
    async save() {
      try {
        this.submitting = true

        let result

        if (this.matchOpponentFormData.id) {
          result = await MatchOpponentApiService.update({
            id: this.matchOpponentFormData.id,
            name: this.matchOpponentFormData.name
          })
        } else {
          result = await MatchOpponentApiService.create(this.matchOpponentFormData.name)
        }

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.match_opponent_save.success'),
          color: 'success'
        })

        this.$emit('refresh', result.data)

        if (this.matchOpponentFormData.id) {
          EventBus.$emit(this.$events.MATCH_OPPONENT_UPDATED)
        } else {
          EventBus.$emit(this.$events.MATCH_OPPONENT_CREATED)
        }

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.match_opponent_save.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async deleteMatchOpponent() {
      try {
        this.deleting = true

        await MatchOpponentApiService.delete(this.matchOpponentFormData.id)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.match_opponent_delete.success'),
          color: 'success'
        })

        this.deleteConfirmationDialogVisible = false

        EventBus.$emit(this.$events.MATCH_OPPONENT_DELETED, this.matchOpponentFormData)
        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.match_opponent_delete.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.deleting = false
      }
    }
  }
}
</script>
